import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { Api } from '@workspace/lib/api'
import { logout, selectToken } from '@/shared/store/auth.store'
import { refreshWithLock } from '@/shared/lib/tokenService'
import { toast } from '@workspace/ui/components/Sonner'

export * from '@workspace/lib/api'

const baseURL = import.meta.env.VITE_PMSVB_API_BASE_URL || 'http://localhost:8080'

export const axiosInstance = axios.create({
    baseURL,
    paramsSerializer: params => {
        const searchParams = new URLSearchParams()
        for (const key in params) {
            const value = params[key]
            if (Array.isArray(value)) {
                value.forEach(v => searchParams.append(key, v))
            } else if (value !== undefined && value !== null) {
                searchParams.append(key, value.toString())
            }
        }
        return searchParams.toString()
    },
})

/**
 * List of public API endpoints that don't require authentication
 * These endpoints should be accessible without an Authorization header
 */
const PUBLIC_API_ENDPOINTS = [
    { method: 'post', url: '/auth/login' },
    { method: 'get', url: '/auth/azure/callback' },
    { method: 'post', url: '/auth/set-password' },
    { method: 'get', url: '/auth/user-by-token' },
    { method: 'post', url: '/auth/refresh' },
]

/**
 * Check if a URL matches any public endpoint
 */
function isPublicEndpoint(url: string | undefined, method: string | undefined = 'get'): boolean {
    if (!url) return false
    return PUBLIC_API_ENDPOINTS.some(endpoint => url.includes(endpoint.url) && method.toLowerCase() === endpoint.method)
}

// Add request interceptor to include auth token
axiosInstance.interceptors.request.use(
    config => {
        // Skip adding auth header for public endpoints
        if (!isPublicEndpoint(config.url, config.method)) {
            const token = selectToken()
            if (token) {
                config.headers.Authorization = `Bearer ${token}`
            }
        }
        return config
    },
    error => {
        return Promise.reject(error)
    },
)

// Add response interceptor for reactive 401 handling
axiosInstance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

        if (isPublicEndpoint(originalRequest.url, originalRequest.method)) {
            return Promise.reject(error)
        }

        // Check if user is blocked or account is not active - redirect to login with blocked state
        const errorData = error.response?.data as any
        const message = errorData?.message || ''
        const errorCode = errorData?.error || errorData?.code || ''
        const status = error.response?.status

        // Check for blocked account based on message and error code only
        const isBlocked =
            message === 'error.userBlocked' ||
            message.includes('userBlocked') ||
            message.includes('blocked') ||
            message.includes('BLOCKED') ||
            message.includes('tạm khóa') ||
            errorCode === 'USER_BLOCKED' ||
            errorCode === 'BLOCKED' ||
            errorCode === 'ACCOUNT_NOT_ACTIVE' ||
            message.includes('not active') ||
            message.includes('không hoạt động')

        if (isBlocked) {
            console.error('[API] User account is blocked or not active', { status, message, errorCode })
            await logout({ skipApi: true, skipRedirect: true })

            // Redirect to login with blocked state
            window.location.href = '/login?blocked=true'
            return Promise.reject(error)
        }

        // Check if error is 401 and we haven't retried yet
        console.log('[API] Response error detected:', {
            status: error.response?.status,
            message,
            errorCode,
            url: originalRequest.url,
            method: originalRequest.method,
        })
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Avoid infinite retry loops
            originalRequest._retry = true

            // Check if this is the refresh endpoint itself
            if (originalRequest.url?.endsWith('/auth/refresh')) {
                console.error('[API] Refresh token is invalid or expired')
                await logout({ skipApi: true })
                toast.error({
                    title: 'Session Expired',
                    description: 'Your session has expired. Please log in again.',
                })
                return Promise.reject(error)
            }

            try {
                console.log('[API] 401 detected, attempting token refresh...')

                // Attempt to refresh token with lock mechanism
                const newToken = await refreshWithLock()

                // Update the authorization header with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`

                // Retry the original request with new token
                console.log('[API] Retrying original request with new token')
                return axiosInstance(originalRequest)
            } catch (refreshError) {
                // Refresh failed, user will be logged out by tokenService
                console.error('[API] Token refresh failed, unable to retry request')
                return Promise.reject(refreshError)
            }
        }

        // For other errors or if retry failed, reject
        return Promise.reject(error)
    },
)

export const api = new Api(axiosInstance)
